export * from './rules';
export * from './script';
export * from './teams';

// Status enum values matching backend
export const IncidentStatus = {
  New: 'New',
  InProgress: 'InProgress',
  OnHold: 'OnHold',
  Resolved: 'Resolved',
  Cancelled: 'Cancelled',
  Closed: 'Closed',
} as const;
export type IncidentStatus =
  (typeof IncidentStatus)[keyof typeof IncidentStatus];

// Priority enum values matching backend (ITIL P1-P4)
export const IncidentPriority = {
  P1: 'P1', // Critical priority
  P2: 'P2', // High priority
  P3: 'P3', // Medium priority
  P4: 'P4', // Low priority
} as const;
export type IncidentPriority =
  (typeof IncidentPriority)[keyof typeof IncidentPriority];

// Impact enum values (ITIL standard)
export const IncidentImpact = {
  High: 'High', // Significant portion of users affected
  Medium: 'Medium', // Department affected
  Low: 'Low', // Individual user affected
} as const;
export type IncidentImpact =
  (typeof IncidentImpact)[keyof typeof IncidentImpact];

// Urgency enum values (ITIL standard)
export const IncidentUrgency = {
  High: 'High', // Action required today
  Medium: 'Medium', // Action required this week
  Low: 'Low', // Can be scheduled
} as const;
export type IncidentUrgency =
  (typeof IncidentUrgency)[keyof typeof IncidentUrgency];

// UserRole enum values matching backend
export const UserRole = {
  SystemAdmin: 'SystemAdmin',
  TenantAdmin: 'TenantAdmin',
  Engineer: 'Engineer',
  Manager: 'Manager',
  CustomerUser: 'CustomerUser',
  ReadOnly: 'ReadOnly',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

// Source enum values matching backend
export const IncidentSource = {
  Manual: 'Manual',
  AzureMonitor: 'AzureMonitor',
  Webhook: 'Webhook',
  Email: 'Email',
  API: 'API',
  Teams: 'Teams',
} as const;
export type IncidentSource =
  (typeof IncidentSource)[keyof typeof IncidentSource];

// IncidentAction enum values matching backend
export const IncidentAction = {
  StartProgress: 'StartProgress',
  Resolve: 'Resolve',
  Close: 'Close',
  PutOnHold: 'PutOnHold',
  Cancel: 'Cancel',
  Assign: 'Assign',
  Unassign: 'Unassign',
  UpdateImpactUrgency: 'UpdateImpactUrgency',
  UpdateDescription: 'UpdateDescription',
  AssignToTeam: 'AssignToTeam',
  UnassignTeam: 'UnassignTeam',
  SetMajorIncident: 'SetMajorIncident',
  UpdateTitle: 'UpdateTitle',
  UpdateCustomer: 'UpdateCustomer',
  UpdateCaller: 'UpdateCaller',
  SetPrimaryCi: 'SetPrimaryCi',
  ClearPrimaryCi: 'ClearPrimaryCi',
  SetBusinessService: 'SetBusinessService',
  ClearBusinessService: 'ClearBusinessService',
} as const;
export type IncidentAction =
  (typeof IncidentAction)[keyof typeof IncidentAction];

// Endpoint type enum values matching backend
export const EndpointType = {
  AzureMonitor: 'AzureMonitor',
  Datadog: 'Datadog',
  Prometheus: 'Prometheus',
  Generic: 'Generic',
  AwsCloudWatch: 'AwsCloudWatch',
  Grafana: 'Grafana',
  NewRelic: 'NewRelic',
} as const;
export type EndpointType = (typeof EndpointType)[keyof typeof EndpointType];

// CI Tag Source enum values matching backend
export const CiTagSource = {
  Manual: 0,
  Discovered: 1,
} as const;
export type CiTagSource = (typeof CiTagSource)[keyof typeof CiTagSource];

export const CiRelationType = {
  Affected: 'Affected',
  Impacted: 'Impacted',
  Caused: 'Caused',
  RelatedTo: 'RelatedTo',
} as const;
export type CiRelationType = (typeof CiRelationType)[keyof typeof CiRelationType];

export const CiImpactLevel = {
  High: 'High',
  Medium: 'Medium',
  Low: 'Low',
} as const;
export type CiImpactLevel = (typeof CiImpactLevel)[keyof typeof CiImpactLevel];

// Processing step type enum values matching backend
export const ProcessingStepType = {
  Validation: 'Validation',
  Normalization: 'Normalization',
  Deduplication: 'Deduplication',
  Suppression: 'Suppression',
  Enrichment: 'Enrichment',
  Correlation: 'Correlation',
  IncidentGate: 'IncidentGate',
  IncidentCreation: 'IncidentCreation',
  Notification: 'Notification',
  Initialization: 'Initialization',
  Custom: 'Custom',
} as const;
export type ProcessingStepType =
  (typeof ProcessingStepType)[keyof typeof ProcessingStepType];

// Processing step status enum values matching backend
export const ProcessingStepStatus = {
  InProgress: 'InProgress',
  Completed: 'Completed',
  Failed: 'Failed',
  Skipped: 'Skipped',
  Pending: 'Pending',
} as const;
export type ProcessingStepStatus =
  (typeof ProcessingStepStatus)[keyof typeof ProcessingStepStatus];

// SLA Policy Scope enum values matching backend
export const SlaPolicyScope = {
  TenantWide: 'TenantWide',
  Customer: 'Customer',
  Team: 'Team',
} as const;
export type SlaPolicyScope =
  (typeof SlaPolicyScope)[keyof typeof SlaPolicyScope];

// SLA Start Trigger enum values matching backend
export const SlaStartTrigger = {
  OnIncidentCreated: 0,
  OnAssignment: 1,
  OnAcknowledgement: 2,
  OnInvestigationStart: 3,
} as const;
export type SlaStartTrigger =
  (typeof SlaStartTrigger)[keyof typeof SlaStartTrigger];

// SLA Stop Trigger enum values matching backend
export const SlaStopTrigger = {
  OnResolution: 0,
  OnAcknowledgement: 1,
  OnClosure: 2,
  OnAssignment: 3,
  OnInvestigationStart: 4,
} as const;
export type SlaStopTrigger =
  (typeof SlaStopTrigger)[keyof typeof SlaStopTrigger];

// ResolveReason enum values matching backend (ITIL standard)
export const ResolveReason = {
  SolutionImplemented: 'SolutionImplemented',
  SolutionProvided: 'SolutionProvided',
  KnownError: 'KnownError',
  Duplicate: 'Duplicate',
  NoCallerResponse: 'NoCallerResponse',
} as const;
export type ResolveReason = string;

// OnHoldReason enum values matching backend (ITIL standard)
export const OnHoldReason = {
  AwaitingCaller: 'AwaitingCaller',
  AwaitingChange: 'AwaitingChange',
  AwaitingVendor: 'AwaitingVendor',
  AwaitingApproval: 'AwaitingApproval',
} as const;
export type OnHoldReason = string;

// CancelReason enum values matching backend (ITIL compliance)
export const CancelReason = {
  Duplicate: 'Duplicate',
  FalseAlarm: 'FalseAlarm',
  CreatedInError: 'CreatedInError',
  UserCancelled: 'UserCancelled',
  NoLongerRequired: 'NoLongerRequired',
  TestIncident: 'TestIncident',
  Other: 'Other',
} as const;
export type CancelReason = string;

// Main data types
export interface Incident {
  id: string;
  tenantId: string;
  customerId: string;
  customerName?: string;
  number: string;
  title: string;
  description: string;
  htmlContent?: string | null;
  status: IncidentStatus;
  impact: IncidentImpact;
  urgency: IncidentUrgency;
  priority: IncidentPriority;
  source: IncidentSource;
  assignedToId?: string;
  assignedToName?: string;
  assignedTeamId?: string;
  assignedTeamName?: string;
  callerCustomerUserId?: string;
  callerCustomerUserName?: string;
  callerSystemUserId?: string;
  callerSystemUserName?: string;
  callerText?: string;
  primaryCiId?: string;
  primaryCiName?: string;
  businessServiceId?: string;
  businessServiceName?: string;
  azureResourceId?: string;
  externalReferenceId?: string;
  createdAt: string;
  updatedAt?: string;
  acknowledgedAt?: string;
  resolvedAt?: string;
  closedAt?: string;
  resolveReason?: ResolveReason;
  resolveNotes?: string;
  onHoldReason?: OnHoldReason;
  onHoldNotes?: string;
  cancelReason?: CancelReason;
  cancelNotes?: string;
  firstResponseSlaTarget?: string;
  resolutionSlaTarget?: string;
  isMajorIncident?: boolean;
  firstResponseSlaBreached: boolean;
  resolutionSlaBreached: boolean;
  slaPausedAt?: string;
  totalSlaPausedMinutes: number;
  slaPolicyId?: string;
  slaPolicyName?: string;
  escalationPolicyId?: string;
  escalationPolicyName?: string;
  slaStatus: string;
  activeSlaPolicies: IncidentSlaPolicy[];
  reopenCount: number;
  reassignmentCount: number;
  eventCount: number;
}

export interface IncidentWorkspaceSummary extends Incident {
  commentCount: number;
  attachmentCount: number;
  slaRecordCount: number;
  currentUserIsAssignedTeamMember: boolean;
}

export interface IncidentSlaPolicy {
  id: string;
  slaPolicyId: string;
  slaPolicyName: string;
  slaType?: string;
  slaLabel?: string;
  appliedAt: string;
  target: string;
  startedAt?: string;
  completedAt?: string;
  pausedAt?: string;
  isBreached: boolean;
  breachedAt?: string;
  isActive: boolean;
  status: string;
  remainingTime?: number; // in milliseconds
}

export interface IncidentHistory {
  id: string;
  incidentId: string;
  changeType: string;
  fieldName: string;
  oldValue?: string;
  newValue?: string;
  changedByUserId: string | null;
  changedByUserName?: string;
  ingestionEndpointId?: string;
  ingestionEndpointName?: string;
  changedAt: string;
  notes?: string;
}

export interface IncidentSlaHistoryEntry {
  slaPolicyName: string;
  incidentSlaPolicyId?: string;
  fromStatus: string;
  toStatus: string;
  changedAt: string;
  changedByUserName?: string;
  reason?: string;
  totalPausedMinutes?: number;
}

export type SlaRecordKind = 'Sla' | 'Ola' | 'Uc';
export type SlaRecordTargetKind = 'FirstResponse' | 'Resolution' | 'UpdateInterval';
export type SlaRecordStage = 'InProgress' | 'Paused' | 'Completed' | 'Cancelled';
export type SlaNotCountingReason = 'OutsideSchedule' | 'Paused' | 'Terminal';
export type SlaRecordScope = 'TenantWide' | 'Customer' | 'Team';
export type SlaScheduleFallbackReasonCode =
  | 'NoScheduleAssigned'
  | 'NoActiveScheduleVersion'
  | 'LegacyBusinessHoursDisabled'
  | 'NoBusinessHoursConfigured';

/**
 * ServiceNow-style "Task SLA" counter row for an incident. One row per SLA
 * counter (e.g. First Response, Resolution). All elapsed/remaining/percentage
 * values are computed server-side against the applicable schedule — the client
 * must never recompute business time.
 */
export interface IncidentSlaRecord {
  id: string;
  slaPolicyId: string;
  slaDefinitionName: string;
  kind: SlaRecordKind;
  targetKind: SlaRecordTargetKind;
  stage: SlaRecordStage;
  hasBreached: boolean;
  targetDurationMinutes: number;
  appliedAt: string;
  startedAt: string | null;
  targetAt: string;
  completedAt: string | null;
  cancelledAt: string | null;
  breachedAt: string | null;
  pausedAt: string | null;
  totalPausedMinutes: number;
  businessElapsedMinutes: number;
  businessRemainingMinutes: number | null;
  businessElapsedPercentage: number | null;
  calendarElapsedMinutes: number;
  calendarRemainingMinutes: number | null;
  scheduleName: string | null;
  is24x7: boolean;
  scope: SlaRecordScope;
  cancellationReasonCode: string | null;
  cancellationReason: string | null;
  supersededById: string | null;
  isCountingNow: boolean;
  notCountingReason: SlaNotCountingReason | null;
  nextCountingResumesAt: string | null;
  countingWindowEndsAt: string | null;
  computedAtUtc: string;
  isUsingFallbackSchedule: boolean;
  fallbackScheduleReasonCode: SlaScheduleFallbackReasonCode | null;
}

export interface IncidentListResponse {
  incidents: Incident[];
  groups?: Array<{
    groupKey: string;
    groupLabel: string;
    itemCount: number;
  }>;
  groupedBy?: string;
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface IncidentLinkedSummary {
  id: string;
  number: string;
  title: string;
  status: IncidentStatus;
  priority: IncidentPriority;
}

export interface ReportSummary {
  totalIncidents: number;
  openIncidents: number;
  resolvedIncidents: number;
  slaBreachedIncidents: number;
  averageMttrMinutes: number;
  averageMttaMinutes: number;
  slaBreachRate: number;
  slaComplianceRate: number | null;
}

export interface TimePeriodMetric {
  period: string;
  value: number;
}

export interface VolumeTrendMetric {
  period: string;
  createdCount: number;
  resolvedCount: number;
  openCount: number;
}

export interface PriorityBreakdown {
  priority: string;
  count: number;
}

export interface StatusBreakdown {
  status: string;
  count: number;
}

export interface CustomerBreakdown {
  customerId: string;
  customerName: string;
  count: number;
  averageMttrMinutes: number;
  slaBreachRate: number;
}

export interface TeamBreakdown {
  teamId?: string;
  teamName: string;
  assignedCount: number;
  resolvedCount: number;
  averageMttrMinutes: number;
  averageMttaMinutes: number;
}

export interface SlaBreachItem {
  incidentId: string;
  incidentTitle: string;
  customerName: string;
  policyName?: string;
  targetAt?: string;
  breachedAt?: string;
}

export interface TargetKindCompliance {
  targetKind: string;
  measured: number;
  met: number;
  breached: number;
  excludedCancelled: number;
  excludedPending: number;
  complianceRate: number | null;
}

export interface SlaKindCompliance {
  kind: string;
  targets: TargetKindCompliance[];
  totalMeasured: number;
  totalMet: number;
  totalBreached: number;
  totalExcludedCancelled: number;
  totalExcludedPending: number;
  complianceRate: number | null;
}

export interface TimeAttribution {
  customerMinutes: number;
  supplierMinutes: number;
  internalMinutes: number;
  outOfHoursMinutes: number;
  supplierEngagementCount: number;
  supplierEngagementMinutes: number;
}

export interface SlaAttainmentBreakdown {
  sla: SlaKindCompliance;
  ola: SlaKindCompliance;
  uc: SlaKindCompliance;
  timeAttribution: TimeAttribution;
}

/**
 * Phase 12: compact overview shape for one clock kind (SLA / OLA / UC). Consumed by the
 * Reports Overview donuts. All figures come from the unified `IncidentAgreementClock` table.
 * `attainmentPercent` is null when no clocks were measured (denominator zero) so the UI can
 * render "n/a" instead of a fabricated 100 %.
 */
export interface ClockCompliance {
  kind: string;
  attainedCount: number;
  breachedCount: number;
  totalClocks: number;
  attainmentPercent: number | null;
}

export interface ReportMetrics {
  summary: ReportSummary;
  mttrTrend: TimePeriodMetric[];
  mttaTrend: TimePeriodMetric[];
  resolutionSlaComplianceTrend: TimePeriodMetric[];
  olaComplianceTrend: TimePeriodMetric[];
  ucComplianceTrend: TimePeriodMetric[];
  volumeTrend: VolumeTrendMetric[];
  byPriority: PriorityBreakdown[];
  byStatus: StatusBreakdown[];
  byCustomer: CustomerBreakdown[];
  byTeam: TeamBreakdown[];
  recentSlaBreaches: SlaBreachItem[];
  recentOlaBreaches: SlaBreachItem[];
  recentUcBreaches: SlaBreachItem[];
  slaAttainment?: SlaAttainmentBreakdown;
  /** Phase 12 — overview-level SLA compliance donut source. */
  slaCompliance?: ClockCompliance;
  /** Phase 12 — overview-level OLA compliance donut source. */
  olaCompliance?: ClockCompliance;
  /** Phase 12 — overview-level UC compliance donut source. */
  ucCompliance?: ClockCompliance;
}

export interface ReportDefinition {
  id: string;
  name: string;
  purpose: string;
  audience: string;
  dataSources: string;
  calculationNotes?: string | null;
  frequency: string;
  format: string;
  owner: string;
  approvedByUserId?: string | null;
  approvedByName?: string | null;
  approvedAt?: string | null;
  isApproved: boolean;
  version: number;
  isActive: boolean;
  createdAt: string;
}

export interface ReportInstance {
  id: string;
  reportDefinitionId: string;
  reportDefinitionName: string;
  periodStart: string;
  periodEnd: string;
  generatedAt: string;
  generatedByUserId: string;
  generatedByName?: string | null;
  dataAsOf: string;
  parametersJson: string;
  payloadJson: string;
  artifactHash: string;
  integrityVerified: boolean;
  distributedAt?: string | null;
  distributedTo?: string | null;
  restatementOfId?: string | null;
  restatementReason?: string | null;
}

// Phase 10 — distribution / shareable-link types --------------------------------------------

/** Report-recipient discriminator. Kept as a const object so it survives Vite's erasable-syntax rules (no TS enums). */
export const ReportRecipientKind = {
  User: 'User',
  CustomerUser: 'CustomerUser',
  Team: 'Team',
  ExternalEmail: 'ExternalEmail',
} as const;
export type ReportRecipientKind = (typeof ReportRecipientKind)[keyof typeof ReportRecipientKind];

export const ReportRecipientDeliveryStatus = {
  Queued: 'Queued',
  Sent: 'Sent',
  Failed: 'Failed',
  Bounced: 'Bounced',
} as const;
export type ReportRecipientDeliveryStatus = (typeof ReportRecipientDeliveryStatus)[keyof typeof ReportRecipientDeliveryStatus];

export interface ReportShareToken {
  id: string;
  tenantId: string;
  reportInstanceId: string;
  /** Raw token — only populated on the create response. */
  token: string | null;
  tokenMasked: string;
  createdAt: string;
  createdByUserId: string;
  expiresAt: string;
  revokedAt: string | null;
  revokedByUserId: string | null;
  accessCount: number;
  lastAccessedAt: string | null;
  isShareSafe: boolean;
  isRevoked: boolean;
  isExpired: boolean;
  publicUrl: string;
}

export interface ReportRecipient {
  id: string;
  tenantId: string;
  reportInstanceId: string;
  recipientKind: ReportRecipientKind;
  recipientReferenceId: string | null;
  externalEmail: string | null;
  resolvedDisplayName: string | null;
  resolvedEmail: string | null;
  deliveredAt: string | null;
  deliveryStatus: ReportRecipientDeliveryStatus;
  deliveryError: string | null;
  shareTokenId: string | null;
  addedByUserId: string;
  addedAt: string;
}

export interface AddRecipientInput {
  kind: ReportRecipientKind;
  referenceId?: string | null;
  externalEmail?: string | null;
}

export interface SendReportResponse {
  reportInstanceId: string;
  shareTokenId: string | null;
  shareTokenMasked: string | null;
  recipients: ReportRecipient[];
}

export interface PublicSharedReport {
  tenantId: string;
  tenantName: string;
  tenantLogoUrl: string | null;
  tenantPrimaryColor: string | null;
  reportInstanceId: string;
  reportName: string;
  periodStart: string;
  periodEnd: string;
  generatedAt: string;
  expiresAt: string;
  payloadJson: string;
}

export interface TopCiItem {
  ciId?: string;
  ciName: string;
  count: number;
}

export interface TopCis {
  items: TopCiItem[];
}

export interface IncidentVolumeTrendItem {
  period: string;
  count: number;
}

export interface IncidentVolumeTrends {
  groupBy: string;
  items: IncidentVolumeTrendItem[];
}

export interface MttrByGroupItem {
  teamId?: string;
  teamName: string;
  averageMttrMinutes: number;
  resolvedCount: number;
}

export interface MttrByGroup {
  useSlaBusinessHours: boolean;
  items: MttrByGroupItem[];
}

export interface SlaCompliance {
  measuredCounters: number;
  metCounters: number;
  breachedCounters: number;
  excludedCancelled: number;
  excludedPending: number;
  complianceRate: number | null;
}

export interface PriorityDistributionItem {
  priority: string;
  count: number;
}

export interface PriorityDistribution {
  items: PriorityDistributionItem[];
}

export interface TopIncidentSourceItem {
  source: string;
  count: number;
}

export interface TopIncidentSources {
  items: TopIncidentSourceItem[];
}

export interface ResolutionTimeBucket {
  bucket: string;
  count: number;
}

export interface ResolutionTimeDistribution {
  buckets: ResolutionTimeBucket[];
}

export interface RecurringIncidentItem {
  normalizedTitle: string;
  sampleTitle: string;
  count: number;
}

export interface RecurringIncidents {
  items: RecurringIncidentItem[];
}

export interface TopReopenedIncidentItem {
  incidentId: string;
  number: string;
  title: string;
  reopenCount: number;
  status: string;
  priority: string;
  customerId?: string;
  customerName?: string;
  assignedTeamId?: string;
  assignedTeamName?: string;
  createdAt: string;
}

export interface TopReopenedIncidents {
  items: TopReopenedIncidentItem[];
}

export interface TopReassignedIncidentItem {
  incidentId: string;
  number: string;
  title: string;
  reassignmentCount: number;
  status: string;
  priority: string;
  customerId?: string;
  customerName?: string;
  assignedTeamId?: string;
  assignedTeamName?: string;
  createdAt: string;
}

export interface TopReassignedIncidents {
  items: TopReassignedIncidentItem[];
}

export interface FirstTimeFixRate {
  eligibleResolvedOrClosedCount: number;
  firstTimeFixCount: number;
  reopenedCount: number;
  rate: number;
}

export interface Comment {
  id: string;
  incidentId: string;
  tenantId: string;
  createdByUserId: string | null;
  createdByUserName?: string;
  content: string;
  commentType: CommentType;
  isInternal: boolean;
  createdAt: string;
  updatedAt?: string;
  editedAt?: string | null;
  isEdited: boolean;
  canEdit: boolean;
  isEditable?: boolean;
  isVisibleToCustomer?: boolean;
}

export const CommentType = {
  ChangeRecord: 0,
  Comment: 1,
  InternalComment: 2,
  SystemMessage: 3,
} as const;

export type CommentType = (typeof CommentType)[keyof typeof CommentType];

export interface Attachment {
  id: string;
  incidentId: string;
  tenantId: string;
  fileName: string;
  fileSizeBytes: number;
  fileSize?: number;
  contentType: string;
  blobUri?: string;
  blobUrl?: string;
  uri?: string;
  uploadedByUserId: string;
  uploadedByName?: string | null;
  uploadedBy?: string;
  createdAt: string;
  uploadedAt?: string;
}

export interface IncidentRelationPageParams {
  skip: number;
  take: number;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  search?: string;
}

export interface IncidentAttachmentPageParams extends IncidentRelationPageParams {
  contentType?: string;
}

export interface IncidentCiPageParams extends IncidentRelationPageParams {
  relationType?: CiRelationType;
  impactLevel?: CiImpactLevel;
  configurationItemId?: string;
}

export interface IncidentCiPage extends PagedResult<IncidentConfigurationItemDto> {
  relationCounts: Record<CiRelationType, number>;
}

export interface IncidentSlaPageParams extends IncidentRelationPageParams {
  stage?: SlaRecordStage;
  kind?: SlaRecordKind;
  targetKind?: SlaRecordTargetKind;
  hasBreached?: boolean;
  includeCancelled?: boolean;
  recordId?: string;
}

export interface IncidentEpisodeSummary {
  id: string;
  title: string;
  status: import('./episode').EpisodeStatus;
  correlationKey: string;
  alertCount: number;
  firstAlertAt: string;
  lastAlertAt: string;
  closedAt: string | null;
  isEscalated: boolean;
}

export interface IncidentEpisodePageParams extends IncidentRelationPageParams {
  status?: import('./episode').EpisodeStatus;
}

export interface CreateIncidentDto {
  customerId: string;
  assignedTeamId?: string;
  title: string;
  description: string;
  impact: IncidentImpact;
  urgency: IncidentUrgency;
  priority: IncidentPriority;
  source?: IncidentSource;
  azureResourceId?: string;
  callerCustomerUserId?: string;
  callerSystemUserId?: string;
  callerText?: string;
}

export interface UpdateIncidentDto {
  action: IncidentAction;
  status?: IncidentStatus;
  impact?: IncidentImpact;
  urgency?: IncidentUrgency;
  priority?: IncidentPriority;
  assignedToId?: string;
  assignedTeamId?: string;
  customerId?: string;
  callerCustomerUserId?: string;
  notes?: string;
  title?: string;
  description?: string;
  resolveReason?: ResolveReason;
  onHoldReason?: OnHoldReason;
  cancelReason?: CancelReason;
  isMajorIncident?: boolean;
}

export interface CreateCommentDto {
  content: string;
  isInternal: boolean;
}

export interface User {
  id: string;
  tenantId: string;
  email: string;
  fullName?: string; // Optional - not set for invited users
  role: UserRole;
  phoneNumber?: string;
  isActive?: boolean;
  status?: UserStatus; // New field
  isInvitation?: boolean; // New field
  invitationId?: string; // New field
  invitedByFullName?: string; // New field
  expiresAt?: string; // New field - for invitations
  externalId?: string;
  identityProvider?: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface UserTeamDto {
  id: string;
  name: string;
}

export interface UserPublicProfileDto {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phoneNumber?: string;
  avatarUrl?: string;
  jobTitle?: string;
  isActive: boolean;
  teams: UserTeamDto[];
}

// UserStatus enum matching backend
export const UserStatus = {
  Active: 'Active',
  Inactive: 'Inactive',
  Invited: 'Invited',
  InvitationExpired: 'InvitationExpired',
  InvitationCancelled: 'InvitationCancelled',
} as const;
export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];

export interface InviteUserRequest {
  email: string;
  invitedRole: UserRole;
  personalMessage?: string;
}

export interface InvitationResponse {
  invitationId: string;
  invitationLink: string;
  expiresAt: string;
}

export interface ResendInvitationResponse {
  invitationId: string;
  invitationLink: string;
  expiresAt: string;
  emailSent: boolean;
}

export interface AcceptInvitationRequest {
  token: string;
  firstName: string;
  lastName: string;
  password: string;
  phoneNumber?: string;
}

export interface AcceptInvitationResponse {
  userId: string;
  tenantId: string;
  email: string;
  token: string; // JWT token for auto-login
}

export interface Customer {
  id: string;
  tenantId: string;
  name: string;
  code: string;
  contactEmail?: string;
  contactPhone?: string;
  isActive: boolean;
  hasCustomPriorityConfiguration?: boolean;
  autoCloseDays?: number; // null = use tenant default, 0 = disabled, >0 = custom override
  createdAt: string;
  updatedAt?: string;
}

export interface CreateCustomerDto {
  name: string;
  code: string;
  contactEmail?: string;
  contactPhone?: string;
}

export interface UpdateCustomerDto {
  name: string;
  code: string;
  contactEmail?: string;
  contactPhone?: string;
  isActive: boolean;
  autoCloseDays?: number; // null = use tenant default, 0 = disabled, >0 = custom override
}

export interface CustomerUser {
  id: string;
  tenantId: string;
  customerId: string;
  name: string;
  email: string;
  phoneNumber?: string;
  jobTitle?: string;
  department?: string;
  isActive: boolean;
  notificationsEnabled: boolean;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateCustomerUserDto {
  customerId: string;
  name: string;
  email: string;
  phoneNumber?: string;
  jobTitle?: string;
  department?: string;
  notificationsEnabled?: boolean;
  sendInvitation?: boolean;
}

export interface UpdateCustomerUserDto {
  name: string;
  email: string;
  phoneNumber?: string;
  jobTitle?: string;
  department?: string;
  notificationsEnabled: boolean;
}

export interface BusinessHoursDto {
  startTime: string; // HH:mm format
  endTime: string; // HH:mm format
  businessDays: number[]; // Array of DayOfWeek (0-6)
  timeZoneId: string; // IANA timezone ID
}

export interface SlaPolicy {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  firstResponseMinutes: number | null;
  resolutionMinutes: number | null;
  warningThresholdMinutes: number | null;
  priorityTargets: SlaPolicyPriorityTargetDto[];
  isActive: boolean;
  scope: SlaPolicyScope;
  scopeCustomerId?: string;
  scopeTeamId?: string;
  useBusinessHoursOnly: boolean;
  businessHours?: BusinessHoursDto;
  holidayCalendarId?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateSlaPolicyDto {
  tenantId: string;
  name: string;
  description?: string;
  firstResponseMinutes: number | null;
  resolutionMinutes: number | null;
  warningThresholdMinutes: number | null;
  priorityTargets: SlaPolicyPriorityTargetDto[];
  scope: SlaPolicyScope;
  scopeCustomerId?: string;
  scopeTeamId?: string;
  useBusinessHoursOnly?: boolean;
  businessHours?: BusinessHoursDto;
  holidayCalendarId?: string;
  recalculateOpenIncidents?: boolean;
}

export interface SlaPolicyPriorityTargetDto {
  priority: number;
  firstResponseMinutes: number | null;
  resolutionMinutes: number | null;
  warningThresholdMinutes: number | null;
}

export interface HolidayCalendar {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  countryCode: string;
  isCustom: boolean;
  isActive: boolean;
}

export interface TenantBranding {
  id?: string;
  tenantId?: string;
  commentColor: string;
  internalCommentColor: string;
  systemMessageColor: string;
  changeRecordColor: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  backgroundColor?: string;
  surfaceColor?: string;
  textColor?: string;
  themeBase: 'light' | 'dark';
}

export interface TenantBrandingData {
  commentColor: string;
  internalCommentColor: string;
  systemMessageColor: string;
  changeRecordColor: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  backgroundColor?: string;
  surfaceColor?: string;
  textColor?: string;
  themeBase: 'light' | 'dark';
}

export interface LoginRequest {
  email: string;
  password: string;
  tenantId?: string;
}

export interface LoginResponse {
  token: string;
  expiresIn: number;
  refreshToken?: string;
  refreshTokenExpiresIn?: number;
  userId: string;
  tenantId: string;
  email: string;
  fullName: string;
  role: string;
}

export interface RegisterRequest {
  tenantSlug: string;
  tenantName: string;
  email: string;
  fullName: string;
  password: string;
  phoneNumber?: string;
  identityProvider?: string;
  externalId?: string;
}

export interface RegisterResponse {
  tenantId: string;
  userId: string;
  tenantSlug: string;
  email: string;
  fullName: string;
  role: string;
  token: string;
  expiresIn: number;
}

export interface RegistrationStatusResponse {
  /** Whether new organizations may currently self-register. Enforced by the server. */
  enabled: boolean;
}

export interface AuthUser {
  userId: string;
  tenantId: string;
  email: string;
  role: string;
  isAuthenticated: boolean;
  permissions?: string[];
  teamIds?: string[];
}

export interface UserOrganizationDto {
  tenantId: string;
  tenantName: string;
  tenantLogo?: string;
  isCurrent: boolean;
  authMethod: string;
}

export interface SwitchTenantResponse {
  token?: string;
  expiresIn?: number;
  refreshToken?: string;
  reauthRequired?: boolean;
  redirectUrl?: string;
  message?: string;
}

export interface HeaderIndicatorsDto {
  slaBreachCount: number;
  slaWarningCount: number;
  slaBreachedIncidentIds: string[];
  slaWarningIncidentIds: string[];
  assignedToMeBreachedIds: string[];
  unassignedCount: number;
}

export interface UpdateUserRoleRequest {
  role: UserRole;
}

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
  token?: string; // Only populated in dev mode
  userId?: string; // Only populated in dev mode
}

export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
}

export interface ColumnMetadataDto {
  id: string;
  label: string;
  type: string;
  sortable: boolean;
  filterable: boolean;
  groupable: boolean;
  defaultVisible: boolean;
  sortField?: string | null;
}

export interface TableCapabilitiesDto {
  supportsGroupBy: boolean;
  supportsExport: boolean;
  supportsMassUpdate: boolean;
  supportsAutoRefresh: boolean;
  supportsCellSelection: boolean;
  groupableColumns: string[];
  editableColumns: string[];
}

export interface TableMetadataDto {
  tableId: string;
  label: string;
  description: string;
  category: string;
  icon: string;
  route: string;
  apiEndpoint: string;
  isSystemTable: boolean;
  requiredPermission: string;
  columns: ColumnMetadataDto[];
  capabilities: TableCapabilitiesDto;
}

// Event Management types
export interface EventProcessingStep {
  id: string;
  tenantId: string;
  rawAlertId: string;
  stepName: string;
  stepType: ProcessingStepType;
  status: ProcessingStepStatus;
  result?: string;
  errorMessage?: string;
  durationMs: number;
  startedAt: string;
  completedAt?: string;
  metadata?: string;
  attemptNumber: number;
}

export const EventStatus = {
  Pending: 'Pending',
  Processing: 'Processing',
  Processed: 'Processed',
  Failed: 'Failed',
  Duplicate: 'Duplicate',
  Suppressed: 'Suppressed',
  Held: 'Held',
  DuplicatePendingLink: 'DuplicatePendingLink',
  SkippedNoIncidentRequired: 'SkippedNoIncidentRequired',
} as const;
export type EventStatus = (typeof EventStatus)[keyof typeof EventStatus];

export interface RawAlertSummary {
  id: string;
  tenantId: string;
  source: string;
  externalAlertId: string;
  endpointId?: string;
  endpointName?: string;
  customerId?: string;
  customerName?: string;
  status: EventStatus;
  incidentId?: string;
  incidentNumber?: string;
  isProcessed: boolean;
  processingError?: string;
  firstFailedAt?: string;
  lastFailedAt?: string;
  receivedAt: string;
  processedAt?: string;
  isDuplicate: boolean;
  duplicateCount: number;
  reprocessCount: number;
  currentAttemptNumber: number;
  duplicateOfAlertId?: string;
  // Normalized fields from pipeline processing
  normalizedTitle?: string;
  normalizedAlertName?: string;
  normalizedMonitorCondition?: string;
  normalizedFiredAt?: string;
  resolvedImpact?: string;
  resolvedUrgency?: string;
  resolvedPriority?: string;
  normalizedResourceName?: string;
  normalizedResourceId?: string;
  normalizedResourceType?: string;
  normalizedResourceGroup?: string;
}

export interface RawAlert extends RawAlertSummary {
  payload: string;
  payloadSha256?: string;
  payloadByteLength?: number;
  payloadContentType?: string;
  payloadIntegrity?: string;
  processedSnapshot?: string;
  detailedErrorLog?: string;
  processingSteps: EventProcessingStep[];
}

export interface EventListResponse {
  items: RawAlertSummary[];
  totalCount: number;
  statusCounts?: Record<string, number>;
}

export interface GetEventsByIncidentParams {
  skip?: number;
  take?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  status?: EventStatus;
  severity?: string;
  source?: string;
  search?: string;
  receivedFrom?: string;
  receivedTo?: string;
  isDuplicate?: boolean;
  configurationItem?: string;
  configurationItemExternalId?: string;
  incidentNumber?: string;
}

export type {
  AlertEpisodeEventSummary,
  AlertEpisode,
  AlertEpisodeListResponse,
  GetEpisodesParams,
  EpisodeStatus,
  Episode,
  EpisodeListResponse,
} from './episode';

export interface GetEventsParams {
  skip?: number;
  take?: number;
  receivedFrom?: string;
  receivedTo?: string;
  customerId?: string;
  endpointId?: string;
  isProcessed?: boolean;
  isDuplicate?: boolean;
  sortBy?: string;
  sortDirection?: string;
  includeCounts?: boolean;
}

export interface IngestionEndpoint {
  id: string;
  tenantId: string;
  customerId?: string;
  name: string;
  description?: string;
  endpointType: EndpointType;
  currentApiKey: string;
  hasPreviousKey: boolean;
  previousKeyExpiresAt?: string;
  isActive: boolean;
  eventCount: number;
  lastEventAt?: string;
  configuration?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateIngestionEndpointDto {
  name: string;
  description?: string;
  endpointType: EndpointType;
  customerId?: string;
  configuration?: string;
}

export interface UpdateIngestionEndpointDto {
  name: string;
  description?: string;
  customerId?: string;
}

export interface RotateApiKeyDto {
  gracePeriodHours?: number;
}

export interface CreateIncidentFromEventsDto {
  eventIds: string[];
  customerId: string;
  title: string;
  description: string;
  severity: string;
  priority: IncidentPriority;
}

// Service Bus Monitoring types
export const QueueStatus = {
  Healthy: 'Healthy',
  Warning: 'Warning',
  Critical: 'Critical',
  Unknown: 'Unknown',
} as const;
export type QueueStatus = (typeof QueueStatus)[keyof typeof QueueStatus];

export interface QueueMetrics {
  queueName: string;
  activeMessageCount: number;
  deadLetterMessageCount: number;
  scheduledMessageCount: number;
  transferMessageCount: number;
  transferDeadLetterMessageCount: number;
  totalMessageCount: number;
  sizeInBytes: number;
  createdAt?: string;
  updatedAt?: string;
  accessedAt?: string;
  status: QueueStatus;
}

export interface DeadLetterMessage {
  sequenceNumber: number;
  messageId: string;
  deadLetterReason?: string;
  deadLetterErrorDescription?: string;
  enqueuedTimeUtc: string;
  deadLetterTimeUtc: string;
  deliveryCount: number;
  contentType?: string;
  size: number;
  body?: string | null;
  correlationId?: string;
  subject?: string;
  applicationProperties?: Record<string, unknown>;
  deadLetterSource?: string;
  expiresAt?: string;
  sessionId?: string;
}

export interface DeadLetterQueueMetrics {
  queueName: string;
  messageCount: number;
  recentMessages: DeadLetterMessage[];
}

export interface DeadLetterPagedResult {
  queueName: string;
  messages: DeadLetterMessage[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface DeadLetterOperationError {
  sequenceNumber: number;
  errorMessage: string;
}

export interface DeadLetterOperationResult {
  totalRequested: number;
  succeeded: number;
  failed: number;
  errors: DeadLetterOperationError[];
}

export interface ServiceBusHealthSummary {
  totalQueues: number;
  healthyQueues: number;
  warningQueues: number;
  criticalQueues: number;
  totalActiveMessages: number;
  totalDeadLetterMessages: number;
  overallStatus: QueueStatus;
  timestamp: string;
}

// Pipeline Configuration types
export const ValidationType = {
  RequiredFields: 0,
  RegexPattern: 1,
  StatusCodeValidation: 2,
  SeverityValidation: 3,
  CustomExpression: 4,
  JsonSchema: 5,
  DataTypeValidation: 6,
} as const;
export type ValidationType =
  (typeof ValidationType)[keyof typeof ValidationType];

export const ValidationSeverity = {
  Info: 0,
  Warning: 1,
  Error: 2,
  Critical: 3,
} as const;
export type ValidationSeverity =
  (typeof ValidationSeverity)[keyof typeof ValidationSeverity];

export interface ValidationRuleDto {
  validationType: ValidationType;
  severity: ValidationSeverity;
  fieldPath: string;
  expectedValue?: string;
  errorMessage?: string;
  order: number;
  isEnabled: boolean;
}

export interface PipelineConfigurationDto {
  id: string;
  tenantId: string;
  ingestionEndpointId: string;
  name: string;
  description?: string;
  validationRulesJson?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export type PipelineNodeType = 'Step' | 'Decision' | 'Merge';

export interface PipelineNodeDto {
  id: string;
  nodeType: PipelineNodeType;
  stepType?: ProcessingStepType;
  name: string;
  positionX: number;
  positionY: number;
  configuration?: string;
  isEnabled: boolean;
  conditionField?: string;
  conditionOperator?: string;
}

export interface PipelineEdgeDto {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  label?: string;
  conditionValue?: string;
  sortOrder: number;
  isDefault: boolean;
}

// Rich condition types for Decision node branches
export type ConditionLogic = 'AND' | 'OR';

export type DecisionOperator =
  | 'equals' | 'notEquals'
  | 'contains' | 'notContains'
  | 'startsWith' | 'endsWith'
  | 'gt' | 'gte' | 'lt' | 'lte' | 'between'
  | 'exists' | 'notExists'
  | 'matches' | 'in' | 'notIn';

export interface ConditionRule {
  field: string;
  operator: DecisionOperator;
  value?: string;
  value2?: string; // For 'between' operator
}

export interface ConditionGroup {
  logic: ConditionLogic;
  conditions: ConditionRule[];
  groups: ConditionGroup[];
}

export interface PipelineGraphDto {
  pipelineConfigurationId: string;
  nodes: PipelineNodeDto[];
  edges: PipelineEdgeDto[];
}

export interface UpdateValidationRulesRequest {
  validationRules: ValidationRuleDto[];
}

export interface ValidationTemplateDto {
  id: string;
  tenantId?: string;
  endpointType: number;
  name: string;
  schemaIdentifier: string;
  validationMode: string;
  rules: string;
  isSystemDefault: boolean;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Settings types
export interface SendGridSettings {
  apiKey: string;
  hasApiKey: boolean;
  fromEmail: string;
  fromName: string;
  enabled: boolean;
}

export interface GeneralSettings {
  supportEmail: string;
  defaultTimezone: string;
  autoCloseDays: number;
  autoCloseEnabled: boolean;
  ciRetirementGracePeriodDays: number;
  autoCloseFrequencyMinutes?: number;
  invitationExpirationDays?: number; // Default: 7 days
}

export interface SystemSettings {
  sendGrid: SendGridSettings;
  general: GeneralSettings;
  assistant: AssistantSettings;
  incidentChatTierCeilings?: IncidentChatTierCeilingsDto;
}

export interface AssistantSettings {
  basicTierModel: string;
  premiumTierModel: string;
  tenantModelOverrides: Record<string, string>;
}

export interface IncidentChatTierCeilingsDto {
  trial: number;
  starter: number;
  professional: number;
  enterprise: number;
}

export interface SendTestEmailRequest {
  toEmail: string;
}

export interface SendTestEmailResult {
  success: boolean;
  message: string;
}

export interface AutoCloseStatus {
  lastRunAt?: string;
  nextRunAt?: string;
  isEnabled: boolean;
  closedIncidentsLastRun: number;
  averageProcessingTime: number;
}

export interface ScheduledJobDto {
  name: string;
  description: string;
  category: string;
  functionApp: string;
  schedule: string | null;
  frequencyDescription: string | null;
  triggerType: string;
}

export interface PipelineDefaultsDto {
  endpointType: string;
  normalization?: unknown;
  deduplication?: unknown;
  suppression?: unknown;
  enrichment?: unknown;
  correlation?: unknown;
  incidentGate?: unknown;
  incidentCreation?: unknown;
  notification?: unknown;
}

export interface PipelineDefaultsListDto {
  endpointTypes: PipelineDefaultsSummaryDto[];
}

export interface PipelineDefaultsSummaryDto {
  endpointType: string;
  hasCustomDefaults: boolean;
  customStepCount: number;
  lastModified?: string;
}

export interface TenantSettings {
  tenantId: string;
  companyName?: string;
  supportEmail?: string;
  timezone?: string;
  autoCloseDays?: number; // null = disabled, >0 = days after resolution before auto-close (default: 7)
  invitationExpirationDays?: number; // Override system default (null = use system default)
  ciRetirementGracePeriodDays?: number; // null = use system default, 0 = disabled, >0 = custom override
  oAuthConfig?: string; // JSON string for OAuth configuration
  samlConfig?: string; // JSON string for SAML configuration
}

export interface AssistantMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  suggestedActions?: { label: string; url: string; actionType: string }[];
  creditsCharged?: number;
  operationType?: string;
  feedback?: 'positive' | 'negative' | null;
}

export interface AssistantConversation {
  conversationId: string;
  messages: AssistantMessage[];
}


export interface TenantEmailSettings {
  tenantId: string;
  useCustomSendGrid: boolean;
  hasSendGridApiKey: boolean;
  sendGridApiKey?: string;
  fromEmail?: string;
  fromName?: string;
}

export interface SlaPlatformDefaults {
  tenantId: string;
  defaultSlaScheduleId?: string | null;
  defaultHolidayCalendarId?: string | null;
  defaultStartTimeSource?: string | null;
  defaultMaxSinglePauseMinutes?: number | null;
  defaultMaxCumulativePauseMinutes?: number | null;
  defaultWarningThresholdMinutes?: number | null;
  defaultWarningThresholdPercentage?: number | null;
  effectiveStartTimeSource: string;
  effectiveWarningThresholdPercentage: number;
}

export interface SendTenantTestEmailRequest {
  toEmail: string;
}

export interface SendTenantTestEmailResult {
  success: boolean;
  message: string;
}
export interface Integration {
  id: string;
  tenantId: string;
  name: string;
  type: string;
  isEnabled: boolean;
  configurationData: string;
  lastTestedAt?: string;
  lastTestResult?: boolean;
  lastTestError?: string;
  isBidirectional: boolean;
  description: string;
  scope: IntegrationScope;
  scopeEntityId?: string;
  channelIdentifier?: string;
}

export const IntegrationScope = {
  Tenant: 0,
  Customer: 1,
  Team: 2,
} as const;

export type IntegrationScope = (typeof IntegrationScope)[keyof typeof IntegrationScope];

export interface AzureDiscoveryConfig {
  azureEnvironment?: string;
  azureTenantId: string;
  clientId: string;
  clientSecret: string;
  subscriptionId?: string;
  subscriptionIds?: string[];
  managementGroupIds?: string[];
  resourceGroups?: string[];
  resourceTypes?: string[];
  defaultCiClassId?: string;
}

export type DiscoveryCircuitState = 'Closed' | 'Open' | 'HalfOpen';

export interface DiscoverySourceDto {
  id: string;
  tenantId: string;
  customerId: string;
  name: string;
  type: string;
  isEnabled: boolean;
  configuration: AzureDiscoveryConfig | string;
  syncSchedule?: string;
  lastSyncAt?: string;
  lastSyncStatus?: string;
  requiresAttention?: boolean;
  requiresAttentionReason?: string | null;
  failureCode?: string | null;
  consecutiveFailures?: number;
  circuitState?: DiscoveryCircuitState;
  nextRetryAt?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface DiscoverySourceListDto {
  items: DiscoverySourceDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface DiscoveryTaskDto {
  id: string;
  discoveryJobId: string;
  subscriptionId: string;
  subscriptionName: string;
  status: string;
  startedAt?: string;
  completedAt?: string;
  elapsedTime?: string;
  itemsDiscovered: number;
  itemsCreated: number;
  itemsUpdated: number;
  itemsUnchanged: number;
  itemsMarkedMissing: number;
  itemsRetired: number;
  errorMessage?: string;
  warnings?: string;
  subscriptionIndex: number;
  totalSubscriptions: number;
}

export interface DiscoveryJobDto {
  id: string;
  discoverySourceId: string;
  sourceName?: string;
  startedAt?: string;
  completedAt?: string;
  status: string;
  cancellationRequested: boolean;
  cancellationReason?: string;
  cancellationRequestedAt?: string;
  canCancel: boolean;
  progressMessage?: string;
  progressPercentage?: number;
  itemsDiscovered: number;
  itemsCreated: number;
  itemsUpdated: number;
  itemsUnchanged: number;
  itemsMarkedMissing: number;
  itemsRetired: number;
  errors?: string;
  errorMessage?: string;
  correlationId?: string;
  logDetails?: string;
  tasks?: DiscoveryTaskDto[];
}

export interface DiscoveryJobListDto {
  items: DiscoveryJobDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber?: number;
  pageSize?: number;
  totalPages?: number;
  statusCounts?: Record<string, number>;
}

export interface CreateDiscoverySourceDto {
  name: string;
  type: number;
  configuration: string;
  syncSchedule?: string;
  isEnabled: boolean;
}

export interface UpdateDiscoverySourceDto {
  name: string;
  configuration: string;
  syncSchedule?: string;
  isEnabled: boolean;
}

// ============================================================================
// Service Connection Types
// ============================================================================

export const ServiceConnectionType = {
  AzureServicePrincipal: 0,
  AwsIamRole: 1,
  GcpServiceAccount: 2,
  ServiceNowApi: 3,
} as const;
export type ServiceConnectionType =
  (typeof ServiceConnectionType)[keyof typeof ServiceConnectionType];

export interface ServiceConnectionDto {
  id: string;
  tenantId: string;
  customerId: string;
  name: string;
  type: ServiceConnectionType;
  configuration: string;
  hasSecret: boolean;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export interface ServiceConnectionListDto {
  items: ServiceConnectionDto[];
  totalCount: number;
}

export interface CreateServiceConnectionDto {
  name: string;
  type: ServiceConnectionType;
  configuration: string;
  clientSecret?: string;
}

export interface UpdateServiceConnectionDto {
  name: string;
  configuration: string;
  clientSecret?: string;
  isEnabled: boolean;
}

// RBAC types for composable roles
export const RoleScope = {
  System: 0,
  Tenant: 1,
  Customer: 2,
} as const;
export type RoleScope = (typeof RoleScope)[keyof typeof RoleScope];

export interface PermissionDto {
  id: string;
  code: string;
  name: string;
  description: string;
  category?: string;
  resourceType?: string;
  action?: string;
  isSystemPermission: boolean;
}

export interface RoleDto {
  id: string;
  tenantId?: string;
  name: string;
  description: string;
  isSystemRole: boolean;
  parentRoleId?: string;
  level: number;
  roleType: number;
  createdAt: string;
  updatedAt?: string;
  permissions: PermissionDto[];
}

export interface CreateRoleRequest {
  name: string;
  description: string;
  parentRoleId?: string;
  permissionIds?: string[];
}

export interface UpdateRoleRequest {
  name: string;
  description: string;
}

export interface DuplicateRoleRequest {
  name: string;
  description: string;
}

export interface AssignPermissionsRequest {
  permissionIds: string[];
}

export interface PermissionCategoryGroup {
  category: string;
  permissions: PermissionDto[];
}

// Team and Assignment types
export interface TeamDto {
  id: string;
  tenantId: string;
  name: string;
  description: string;
  isActive: boolean;
  memberCount: number;
  createdAt: string;
  updatedAt?: string;
}

export interface TeamDetailDto {
  id: string;
  tenantId: string;
  name: string;
  description: string;
  isActive: boolean;
  members: TeamMemberDto[];
  createdAt: string;
  updatedAt?: string;
}

export interface TeamMemberDto {
  teamId: string;
  userId: string;
  userName: string;
  userEmail: string;
  addedAt: string;
}

export interface TeamWorkloadDto {
  teamId: string;
  teamName: string;
  totalIncidents: number;
  unassignedToUserIncidents: number;
  memberWorkloads: MemberWorkloadDto[];
}

export interface MemberWorkloadDto {
  userId: string;
  userName: string;
  assignedIncidents: number;
}

export interface WorkloadSummaryDto {
  globallyUnassignedIncidents: number;
  teamWorkloads: TeamWorkloadDto[];
}

export interface CreateTeamRequest {
  name: string;
  description?: string;
}

export interface UpdateTeamRequest {
  name: string;
  description?: string;
}

// Assignment History
export const AssignmentActionType = {
  AssignedToTeam: 0,
  AssignedToUser: 1,
  UnassignedFromTeam: 2,
  UnassignedFromUser: 3,
  ReassignedTeam: 4,
  ReassignedUser: 5,
} as const;
export type AssignmentActionType =
  (typeof AssignmentActionType)[keyof typeof AssignmentActionType];

export interface IncidentAssignmentHistoryDto {
  id: string;
  incidentId: string;
  assignedTeamId?: string;
  assignedTeamName?: string;
  assignedUserId?: string;
  assignedUserName?: string;
  assignedByUserId: string;
  assignedByUserName?: string;
  assignedAt: string;
  actionType: AssignmentActionType;
  notes?: string;
}

// Team Workload types
export interface TeamWorkloadMember {
  userId: string;
  userName: string;
  userEmail: string;
  assignedIncidents: number;
}

export interface TeamWorkloadSummary {
  teamId: string;
  teamName: string;
  totalOpenIncidents: number;
  unassignedIncidents: number;
  members: TeamWorkloadMember[];
}

export interface TeamsWorkloadResponse {
  teams: TeamWorkloadSummary[];
  totalOpenIncidents: number;
  totalUnassignedIncidents: number;
}

export interface TeamsAnalyticsDto {
  totalNotificationsSent: number;
  notificationsSentToChannels: number;
  notificationsSentToUsers: number;
  notificationsByEventType: Record<string, number>;
  totalCardActionsExecuted: number;
  actionsByType: Record<string, number>;
  totalCommandsExecuted: number;
  commandsByFamily: Record<string, number>;
  averageResponseTimeMinutes: number | null;
  medianResponseTimeMinutes: number | null;
  dynamicChannelsCreated: number;
  activeChannels: number;
  totalLinkedUsers: number;
  activeUsers: number;
  usersByActivity: Record<string, number>;
  totalSyncEvents: number;
  failedSyncEvents: number;
  successRate: number;
}

export interface TeamsEngagementDataPoint {
  date: string;
  notifications: number;
  actions: number;
  commands: number;
}

export interface TeamsUserEngagement {
  userId: string;
  userName: string;
  totalActions: number;
  totalCommands: number;
  lastActivityAt: string;
}

export interface TeamsEngagementDto {
  timeline: TeamsEngagementDataPoint[];
  topUsers: Record<string, TeamsUserEngagement>;
}

export interface FeedbackDto {
  id: string;
  tenantId: string;
  userId: string;
  pageUrl: string;
  feedbackType: string;
  message: string;
  rating?: number;
  status: string;
  createdAt: string;
}

export interface SubmitFeedbackRequest {
  pageUrl: string;
  feedbackType: string;
  message: string;
  rating?: number;
}

export interface BugReportDto {
  id: string;
  tenantId: string;
  fingerprint: string;
  title: string;
  description?: string;
  reproductionSteps?: string;
  resolutionSuggestion?: string;
  severity: string;
  sourceComponent?: string;
  errorMessage?: string;
  stackTraceSignature?: string;
  correlationId?: string;
  occurrenceCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  status: string;
  rootCauseAnalysis?: string;
  isAiAnalyzed: boolean;
  aiAnalyzedAt?: string;
  isAutoGenerated: boolean;
  logEntryCount: number;
}

export interface BugReportLogEntryDto {
  id: string;
  bugReportId: string;
  timestamp: string;
  level: string;
  message: string;
  exception?: string;
  logger: string;
  requestPath?: string;
  correlationId?: string;
  userId?: string;
  sourceSystemLogId: string;
}

export interface BugReportDetailDto extends BugReportDto {
  logEntries: BugReportLogEntryDto[];
}

export interface DeduplicationResultDto {
  totalBefore: number;
  totalAfter: number;
  mergedCount: number;
  uniqueGroups: number;
}

export interface ClientErrorReport {
  message: string;
  stack?: string;
  componentStack?: string;
  source: string;
  pageUrl: string;
  userAgent: string;
  timestamp: string;
}

// Query Performance types
export interface PerformanceSummaryDto {
  totalQueries: number;
  avgDurationMs: number;
  p95DurationMs: number;
  p99DurationMs: number;
  slowQueryCount: number;
  slowQueryPercentage: number;
  mostQueriedTable: string;
  slowestEndpoint: string;
  totalRowsAffected: number;
}

export interface SlowQueryDto {
  id: string;
  timestamp: string;
  requestPath: string;
  tableOrContainer: string;
  operationType: string;
  commandText?: string | null;
  durationMs: number;
  rowsAffected: number;
  tenantId?: string | null;
}

export interface GroupedSlowQueryDto {
  endpointOrQuery: string;
  tableName: string;
  operation: string;
  executionCount: number;
  minDurationMs: number;
  avgDurationMs: number;
  maxDurationMs: number;
  totalDurationMs: number;
  lastSeen: string;
}

export interface EndpointPerformanceDto {
  requestPath: string;
  queryCount: number;
  avgDurationMs: number;
  p95DurationMs: number;
  maxDurationMs: number;
  slowQueryCount: number;
}

export interface TablePerformanceDto {
  tableOrContainer: string;
  queryCount: number;
  avgDurationMs: number;
  maxDurationMs: number;
  totalRowsAffected: number;
  slowQueryCount: number;
}

export interface PerformanceTrendBucketDto {
  bucketStart: string;
  queryCount: number;
  avgDurationMs: number;
  p95DurationMs: number;
  slowQueryCount: number;
}

// System Logs types
export const LogLevel = {
  Trace: 'Trace',
  Debug: 'Debug',
  Information: 'Information',
  Warning: 'Warning',
  Error: 'Error',
  Critical: 'Critical',
  None: 'None',
} as const;
export type LogLevel = (typeof LogLevel)[keyof typeof LogLevel];

export interface SystemLog {
  id: string;
  timestamp: string;
  level: LogLevel;
  message: string;
  logger: string;
  exception?: string;
  userId?: string;
  tenantId?: string;
  requestPath?: string;
  correlationId?: string;
}

export interface SystemLogDto {
  id: string;
  timestamp: string;
  level: number;
  message: string;
  exception?: string;
  logger: string;
  userId?: string;
  tenantId?: string;
  requestPath?: string;
  correlationId?: string;
}

export interface SystemLogsResponse {
  logs: SystemLog[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface SystemLogHealth {
  isDatabaseConnected: boolean;
  lastLogTimestamp?: string;
  totalLogsCount: number;
  logLevelDistribution: LogLevelDistribution;
  errorRates: ErrorRateMetrics;
  recentErrors: RecentError[];
}

export interface LogLevelDistribution {
  trace: number;
  debug: number;
  information: number;
  warning: number;
  error: number;
  critical: number;
}

export interface ErrorRateMetrics {
  last5Minutes: number;
  lastHour: number;
  last24Hours: number;
}

export interface RecentError {
  id: string;
  timestamp: string;
  level: string;
  message: string;
  logger: string;
}

// Sign-In Log types
export interface SignInLog {
  id: string;
  timestamp: string;
  tenantId?: string;
  userId?: string;
  email: string;
  success: boolean;
  errorMessage?: string;
  ipAddress?: string;
  userAgent?: string;
  authMethod?: string;
  identityProvider?: string;
}

export interface SignInLogsResponse {
  logs: SignInLog[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface GetSignInLogsRequest {
  email?: string;
  fromDate?: string;
  toDate?: string;
  success?: boolean;
  authMethod?: string;
  page?: number;
  pageSize?: number;
}

// Avatar upload types
export interface UploadAvatarResponse {
  avatarUrl: string;
}

// SSO Configuration types
export interface SsoOAuthConfig {
  enabled: boolean;
  provider?: string;
  clientId?: string;
  clientSecret?: string;
  authority?: string;
  scopes?: string;
}

export interface SsoSamlConfig {
  enabled: boolean;
  entityId?: string;
  ssoUrl?: string;
  certificate?: string;
  signRequests?: boolean;
}

// AssignmentScope enum values matching backend
export const AssignmentScope = {
  Tenant: 0,
  Customer: 1,
  Team: 2,
} as const;
export type AssignmentScope =
  (typeof AssignmentScope)[keyof typeof AssignmentScope];

// Role Assignment types
export interface RoleAssignment {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  roleId: string;
  roleName: string;
  scope: AssignmentScope;
  scopeEntityId?: string;
  scopeEntityName?: string;
  assignedAt: string;
  assignedBy?: string;
  assignedByName?: string;
  expiresAt?: string;
  isActive: boolean;
}

export interface AssignRoleRequest {
  roleId: string;
  scope: AssignmentScope;
  scopeEntityId?: string;
}

// API Key Management Types
export interface ApiKeyDto {
  id: string;
  name: string;
  keyPrefix: string;
  status: string;
  createdAt: string;
  createdBy: string | null;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  roles: ApiKeyRoleDto[];
}

export interface ApiKeyRoleDto {
  roleId: string;
  roleName: string;
  description?: string;
  isSystemRole?: boolean;
}

export interface CreateApiKeyRequest {
  name: string;
  roleIds: string[];
  expiresAt?: string | null;
}

export interface CreateApiKeyResponse {
  id: string;
  name: string;
  keyPrefix: string;
  rawKey: string;
  createdAt: string;
  expiresAt: string | null;
  roles: ApiKeyRoleDto[];
}

// System Info types
export interface ComponentInfo {
  name: string;
  version?: string;
  commitSha?: string;
  buildTimestamp?: string;
  deployedAt?: string;
  status: string;
  statusMessage?: string;
}

export interface SystemInfoResponse {
  components: ComponentInfo[];
  serverTimestamp: string;
  environment: string;
  runtime: string;
}

// CMDB types
export const FieldType = {
  Text: 0,
  LongText: 1,
  RichText: 2,
  Number: 3,
  Decimal: 4,
  Currency: 5,
  Date: 6,
  DateTime: 7,
  Boolean: 8,
  Dropdown: 9,
  MultiSelect: 10,
  Email: 11,
  Url: 12,
  PhoneNumber: 13,
  FileAttachment: 14,
  RelationshipToUser: 15,
  RelationshipToCustomer: 16,
  RelationshipToCI: 17,
  // Backward-compatible aliases
  TextArea: 1,
  Phone: 13,
  UserReference: 15,
  CustomerReference: 16,
  CiReference: 17,
} as const;
export type FieldType = (typeof FieldType)[keyof typeof FieldType];

export const CiFieldType = FieldType;
export type CiFieldType = FieldType;

export const CiChangeType = {
  Created: 0,
  Updated: 1,
  StatusChanged: 2,
  RelationshipAdded: 3,
  RelationshipRemoved: 4,
  FieldValueChanged: 5,
  Restored: 6,
  VersionCreated: 7,
} as const;
export type CiChangeType = (typeof CiChangeType)[keyof typeof CiChangeType];

export interface ConfigurationItemListDto {
  items: ConfigurationItemDto[];
  groups?: ConfigurationItemGroupDto[];
  groupedBy?: string;
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ConfigurationItemGroupDto {
  groupKey: string;
  groupLabel: string;
  itemCount: number;
  items: ConfigurationItemDto[];
}

// Missing reason enum for retired CIs
export const MissingReason = {
  Unknown: 'Unknown',
  Deleted: 'Deleted',
  PermissionDenied: 'PermissionDenied',
  TemporaryFailure: 'TemporaryFailure',
} as const;
export type MissingReason = (typeof MissingReason)[keyof typeof MissingReason];

// Recently retired CI item
export interface RecentlyRetiredItemDto {
  id: string;
  name: string;
  className: string;
  classIcon: string | null;
  discoverySourceName: string | null;
  retiredAt: string;
  missingReason: MissingReason;
}

// Retirement trend data point (daily count)
export interface RetirementTrendPointDto {
  date: string; // ISO date string
  count: number;
}

// Discovery source breakdown
export interface SourceBreakdownDto {
  sourceId: string;
  sourceName: string;
  sourceType: string; // 'Azure' | 'AWS' | 'GCP' | 'ServiceNow' | etc.
  activeItems: number;
  retiringItems: number;
  retiredItems: number;
  totalItems: number;
  lastSyncAt: string | null;
}

export interface CmdbDashboardDto {
  totalItems: number;
  activeItems: number;
  deletedItems: number; // DEPRECATED: kept for backward compat
  // New lifecycle fields
  retiringItems: number;
  retiredItems: number;
  itemsByClass: { className: string; classIcon: string | null; count: number }[];
  recentlyModified: {
    id: string;
    name: string;
    className: string;
    classIcon: string | null;
    updatedAt: string;
  }[];
  discovery: { sourceCount: number; lastSyncTime: string | null; totalDiscovered: number };
  relationships: { totalRelationships: number; relationshipTypeCount: number };
  // New sections
  recentlyRetired: RecentlyRetiredItemDto[];
  retirementTrend: RetirementTrendPointDto[];
  sourceBreakdown: SourceBreakdownDto[];
}

export interface CiFieldOptionDto {
  id: string;
  customFieldId: string;
  label: string;
  value: string;
  color?: string;
  icon?: string;
  isDefault: boolean;
  displayOrder: number;
  description?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CiCustomFieldDto {
  id: string;
  ciClassId: string;
  ciClassName?: string;
  name: string;
  code: string;
  fieldType: FieldType;
  isRequired: boolean;
  isUnique: boolean;
  defaultValue?: string;
  validationRules?: string;
  displayOrder: number;
  helpText?: string;
  relatedEntityType?: string;
  isInherited?: boolean;
  inheritedFromClassName?: string;
  inheritedFromClassId?: string;
  isActive?: boolean;
  options: CiFieldOptionDto[];
}

export interface CiClassViewDto {
  id: string;
  tenantId: string | null;
  ciClassId: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  isSystemDefault: boolean;
  displayOrder: number;
  fieldCount: number;
  createdAt: string;
}

export interface CiClassViewDetailDto {
  id: string;
  tenantId: string | null;
  ciClassId: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  isSystemDefault: boolean;
  displayOrder: number;
  createdAt: string;
  fields: CiClassViewFieldDto[];
}

export interface CiClassViewFieldDto {
  id: string;
  ciCustomFieldId: string;
  fieldName: string;
  fieldCode: string;
  fieldType: number;
  isRequired: boolean;
  displayOrder: number;
  columnSpan: number;
  sectionName: string | null;
  sectionOrder: number;
  sectionCollapsedByDefault: boolean;
  isVisible: boolean;
}

export interface CiFieldValueDto {
  id?: string;
  configurationItemId?: string;
  customFieldId: string;
  fieldName?: string;
  textValue?: string;
  numberValue?: number;
  booleanValue?: boolean;
  dateValue?: string;
  jsonValue?: string;
}

export interface CreateCiFieldValueDto {
  customFieldId: string;
  textValue?: string;
  numberValue?: number;
  booleanValue?: boolean;
  dateValue?: string;
  jsonValue?: string;
}

export interface CiTagDto {
  id: string;
  configurationItemId: string;
  key: string;
  value: string;
  source: CiTagSource;
  createdAt: string;
}

export interface CreateCiTagDto {
  key: string;
  value: string;
}

export const CiLifecycleStage = {
  Planned: 0,
  InDevelopment: 1,
  Active: 2,
  Retiring: 3,
  Retired: 4,
} as const;

export type CiLifecycleStage = (typeof CiLifecycleStage)[keyof typeof CiLifecycleStage];

export interface ConfigurationItemDto {
  id: string;
  tenantId: string;
  customerId: string;
  customerName?: string;
  ciClassId: string;
  ciClassName?: string;
  name: string;
  discoverySourceId?: string;
  discoverySourceName?: string;
  externalId?: string;
  resourceType?: string;
  resourceJson?: string | null;
  region?: string | null;
  resourceGroup?: string | null;
  subscriptionId?: string | null;
  lastDiscoveredAt?: string;
  lifecycleStage: CiLifecycleStage;
  lastSeenAt?: string;
  isDeleted: boolean;
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  updatedAt?: string;
  updatedBy?: string;
  updatedByName?: string;
  fieldValues: CiFieldValueDto[];
  tags?: CiTagDto[];
  markedMissingSince?: string;
  consecutiveMissedDiscoveryRuns?: number;
  deletionConfidence?: string;
  missingReason?: MissingReason;
  retirementEligibleAt?: string;
  retiredAt?: string;
}

export interface GetConfigurationItemsParams {
  customerId?: string;
  ciClassId?: string;
  lifecycleStage?: CiLifecycleStage;
  searchTerm?: string;
  groupBy?: string;
  page?: number;
  pageSize?: number;
}

export interface CiClassDto {
  id: string;
  tenantId: string;
  parentClassId?: string;
  name: string;
  code: string;
  icon?: string;
  description?: string;
  level: number;
  inheritsFields: boolean;
  isSystem: boolean;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CiRelationshipDto {
  id: string;
  tenantId?: string;
  sourceCiId: string;
  sourceCiName?: string;
  targetCiId: string;
  targetCiName?: string;
  relationshipTypeId: string;
  relationshipTypeName?: string;
  forwardLabel?: string;
  reverseLabel?: string;
  description?: string;
  createdAt: string;
}

export interface CiVersionDto {
  id: string;
  configurationItemId: string;
  versionNumber?: number;
  reason?: string;
  createdAt: string;
  createdBy?: string;
}

export interface CiChangeHistoryDto {
  id: string;
  configurationItemId: string;
  changeType: CiChangeType;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  description?: string;
  changedAt: string;
  changedBy: string;
}

export interface CiHistoryListDto {
  items: CiChangeHistoryDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CiIncidentLinkDto {
  id: string;
  incidentId: string;
  incidentNumber: string;
  title: string;
  incidentTitle: string;
  status?: IncidentStatus;
  incidentStatus?: IncidentStatus;
  statusLabel: string;
  incidentStatusName: string;
  priority: IncidentPriority;
  configurationItemId: string;
  configurationItemName: string;
  relationType: CiRelationType;
  relationTypeLabel: string;
  relationTypeName: string;
  ciClassName?: string;
  impactLevel: CiImpactLevel;
  impactLevelName: string;
  slaResponseTarget?: string;
  slaResponseStatus: string;
  slaResolutionTarget?: string;
  slaResolutionStatus: string;
  createdAt: string;
  updatedAt?: string;
  assignedTo?: string;
  team?: string;
  notes?: string;
  addedAt: string;
  addedBy: string;
}

export type LinkedIncidentDto = CiIncidentLinkDto;

export interface IncidentConfigurationItemDto {
  id: string;
  incidentId: string;
  incidentNumber: string;
  title: string;
  incidentTitle: string;
  status: IncidentStatus | null;
  incidentStatus: IncidentStatus | null;
  statusLabel: string;
  incidentStatusName: string;
  priority: IncidentPriority;
  configurationItemId: string;
  configurationItemName: string;
  ciClassName?: string;
  relationType: CiRelationType;
  relationTypeLabel: string;
  relationTypeName: string;
  impactLevel: CiImpactLevel;
  impactLevelName: string;
  slaResponseTarget?: string;
  slaResponseStatus: string;
  slaResolutionTarget?: string;
  slaResolutionStatus: string;
  createdAt: string;
  updatedAt?: string;
  assignedTo?: string;
  team?: string;
  notes?: string;
  addedAt: string;
  addedBy: string;
}

export interface LinkCiToIncidentDto {
  configurationItemId: string;
  relationType: CiRelationType;
  impactLevel: CiImpactLevel;
  notes?: string;
}

export interface UpdateIncidentCiRelationDto {
  relationType: CiRelationType;
  impactLevel: CiImpactLevel;
  notes?: string;
}

export interface CreateConfigurationItemDto {
  ciClassId: string;
  name: string;
  discoverySourceId?: string;
  externalId?: string;
  fieldValues: CreateCiFieldValueDto[];
}

export interface UpdateConfigurationItemDto {
  name: string;
  fieldValues: CreateCiFieldValueDto[];
}

export interface CreateCiClassDto {
  parentClassId?: string;
  name: string;
  code: string;
  icon?: string;
  description?: string;
  inheritsFields: boolean;
}

export interface UpdateCiClassDto {
  name: string;
  icon?: string;
  description?: string;
}

export interface CreateCiCustomFieldDto {
  name: string;
  code: string;
  fieldType: FieldType;
  isRequired: boolean;
  isUnique: boolean;
  defaultValue?: string;
  validationRules?: string;
  displayOrder: number;
  helpText?: string;
  relatedEntityType?: string;
  options: CreateCiFieldOptionDto[];
}

export interface UpdateCiCustomFieldDto {
  name: string;
  displayOrder: number;
  helpText?: string;
  defaultValue?: string;
  validationRules?: string;
}

export interface CreateCiClassViewRequest {
  name: string;
  description?: string;
  isDefault?: boolean;
}

export interface UpdateCiClassViewRequest {
  name: string;
  description?: string;
  isDefault: boolean;
  displayOrder: number;
}

export interface UpdateCiClassViewFieldsRequest {
  fields: {
    ciCustomFieldId: string;
    displayOrder: number;
    columnSpan: number;
    sectionName?: string;
    sectionOrder: number;
    sectionCollapsedByDefault: boolean;
    isVisible: boolean;
  }[];
}

export interface CreateCiFieldOptionDto {
  label: string;
  value: string;
  color?: string;
  icon?: string;
  isDefault: boolean;
  displayOrder: number;
  description?: string;
}

export interface UpdateCiFieldOptionDto {
  label: string;
  value: string;
  color?: string;
  icon?: string;
  isDefault: boolean;
  displayOrder: number;
  description?: string;
}

export interface ReorderCiFieldOptionsDto {
  items: Array<{
    id: string;
    displayOrder: number;
  }>;
}

export interface CreateCiRelationshipDto {
  sourceCiId: string;
  targetCiId: string;
  relationshipTypeId: string;
  description?: string;
}

export interface CreateCiVersionDto {
  reason?: string;
}

// CMDB Catalog
export interface CiClassCatalogItem {
  id: string;
  name: string;
  code: string;
  icon: string | null;
  description: string | null;
  catalogDescription: string | null;
  isDefaultForNewTenants: boolean;
  isFeatured: boolean;
  isSubscribed: boolean;
  parentClassId: string | null;
  parentClassName: string | null;
  level: number;
}

// Tag Mapping Types
export interface CiTagMappingDto {
  id: string;
  tenantId: string;
  tagKey: string;
  targetCustomFieldId?: string;
  targetCustomFieldName?: string;
  targetBuiltInField?: string;
  ciClassId?: string;
  ciClassName?: string;
  createdAt: string;
  createdBy: string;
}

export interface CreateCiTagMappingDto {
  tagKey: string;
  targetCustomFieldId?: string;
  targetBuiltInField?: string;
  ciClassId?: string;
}

export interface UpdateCiTagMappingDto {
  tagKey: string;
  targetCustomFieldId?: string;
  targetBuiltInField?: string;
  ciClassId?: string;
}

export interface PredefinedTagDto {
  id: string;
  tenantId: string;
  key: string;
  description?: string;
  color?: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreatePredefinedTagDto {
  key: string;
  description?: string;
  color?: string;
  displayOrder: number;
}

export interface UpdatePredefinedTagDto {
  key: string;
  description?: string;
  color?: string;
  displayOrder: number;
  isActive: boolean;
}

export interface AzureHierarchyCustomerDto {
  customerId: string;
  customerName: string;
  managementGroupCount: number;
  subscriptionCount: number;
  totalResourceCount: number;
}

export interface AzureHierarchyNodeDto {
  ciId: string;
  name: string;
  ciClassName: string;
  ciClassCode: string;
  resourceType?: string;
  externalId?: string;
  status?: string;
  childCount: number;
  directChildCount: number;
  children: AzureHierarchyNodeDto[];
}

export interface AzureHierarchyDto {
  roots: AzureHierarchyNodeDto[];
  totalResourceCount: number;
}

export interface PriorityLevelDto {
  value: number;
  name: string;
  color: string;
}

export interface PriorityMatrixEntryDto {
  impactLevel: number;
  urgencyLevel: number;
  priorityLevel: number;
}

export interface TenantPriorityConfigDto {
  id: string;
  tenantId: string;
  customerId?: string;
  priorityLevels: PriorityLevelDto[];
  impactLevels: PriorityLevelDto[];
  urgencyLevels: PriorityLevelDto[];
  priorityMatrix: PriorityMatrixEntryDto[];
  templateName?: string;
  isUsingSystemDefault?: boolean;
  isActive: boolean;
}

export interface SystemPriorityTemplateDto {
  id: string;
  name: string;
  description: string;
  priorityLevels: PriorityLevelDto[];
  impactLevels: PriorityLevelDto[];
  urgencyLevels: PriorityLevelDto[];
  priorityMatrix: PriorityMatrixEntryDto[];
  isDefault: boolean;
  isActive: boolean;
  displayOrder: number;
}

export interface UpdateTenantPriorityConfigRequestDto {
  customerId?: string;
  priorityLevels?: PriorityLevelDto[];
  impactLevels?: PriorityLevelDto[];
  urgencyLevels?: PriorityLevelDto[];
  priorityMatrix?: PriorityMatrixEntryDto[];
  templateName?: string;
  isActive: boolean;
}

export interface ApplyPriorityTemplateRequestDto {
  templateId: string;
  customerId?: string;
}

export interface ReasonDefinitionDto {
  value: string;
  label: string;
  description?: string;
}

export interface OnHoldReasonDefinitionDto extends ReasonDefinitionDto {
  pauseSla: boolean;
}

export interface TenantReasonConfigDto {
  id: string;
  tenantId: string;
  customerId?: string;
  resolveReasons: ReasonDefinitionDto[];
  onHoldReasons: OnHoldReasonDefinitionDto[];
  cancelReasons: ReasonDefinitionDto[];
}

export interface SystemReasonTemplateDto {
  id: string;
  name: string;
  description: string;
  isDefault: boolean;
  resolveReasons: ReasonDefinitionDto[];
  onHoldReasons: OnHoldReasonDefinitionDto[];
  cancelReasons: ReasonDefinitionDto[];
}

export interface SystemSlaPolicyTemplateDto {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  priorityTargetsJson: string;
  priorityTargets?: SlaPriorityTarget[];
  businessHoursOnly: boolean;
  useBusinessHoursOnly?: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface SlaPriorityTarget {
  priority: string;
  firstResponseMinutes: number | null;
  resolutionMinutes: number | null;
}

export interface CreateSystemSlaPolicyTemplateDto {
  name: string;
  description?: string;
  isDefault: boolean;
  priorityTargetsJson: string;
  businessHoursOnly: boolean;
}

export type UpdateSystemSlaPolicyTemplateDto = CreateSystemSlaPolicyTemplateDto;

export type CreateSystemSlaPolicyTemplateRequest = CreateSystemSlaPolicyTemplateDto;
export type UpdateSystemSlaPolicyTemplateRequest = UpdateSystemSlaPolicyTemplateDto;

export interface UpsertSystemPriorityTemplateRequest {
  name: string;
  description?: string;
  isDefault: boolean;
  priorityLevels: PriorityLevelDto[];
  impactLevels: PriorityLevelDto[];
  urgencyLevels: PriorityLevelDto[];
  priorityMatrix: PriorityMatrixEntryDto[];
  isActive?: boolean;
  sortOrder?: number;
}

export interface UpsertSystemReasonTemplateRequest {
  name: string;
  description?: string;
  isDefault: boolean;
  resolveReasons: ReasonDefinitionDto[];
  onHoldReasons: OnHoldReasonDefinitionDto[];
  cancelReasons: ReasonDefinitionDto[];
}

export interface UpdateTenantReasonConfigRequestDto {
  customerId?: string;
  resolveReasons?: ReasonDefinitionDto[];
  onHoldReasons?: OnHoldReasonDefinitionDto[];
  cancelReasons?: ReasonDefinitionDto[];
}

export interface ApplyReasonTemplateRequestDto {
  templateId: string;
  customerId?: string;
}

export * from './cmdb';

// Re-export team types
export * from './team';

export type {
  TransformType,
  FieldMappingRule,
  NormalizationConfig,
  ConditionOperator,
  NormalizationCondition,
  NormalizationProfile,
  ConditionalNormalization,
  DedupStrategy,
  MatchStrategy,
  GroupingStrategy,
  DeduplicationSettings,
  GroupingSettings,
  DeduplicationGroupingConfig,
  FieldSuggestion,
} from './pipelineConfig';

export interface VersionHistoryResponse {
  currentVersion: string;
  versions: VersionEntry[];
}

export interface VersionEntry {
  version: string;
  releasedAt: string;
  summary: string;
  changes: VersionChange[];
}

export interface VersionChange {
  category:
    | 'feature'
    | 'fix'
    | 'improvement'
    | 'breaking'
    | 'security'
    | 'documentation'
    | 'test'
    | 'removed'
    | 'note';
  description: string;
}

export interface WhatsNewResponse {
  hasUpdates: boolean;
  currentVersion: string;
  entries: VersionEntry[];
}

// Navigation Quick Access
export type NavLinkScope = 'User' | 'Tenant' | 'System';

export interface SavedNavLink {
  id: string;
  tenantId?: string;
  userId?: string;
  scope: NavLinkScope;
  name: string;
  description?: string;
  icon?: string;
  sectionName?: string;
  sectionIcon?: string;
  targetPath: string;
  filterConfigJson?: string;
  columnConfigJson?: string;
  sortConfigJson?: string;
  sharedWithCustomerIds?: string[];
  sharedWithTeamIds?: string[];
  sharedWithUserIds?: string[];
  displayOrder: number;
  requiredPermission?: string;
  canBeModifiedByOthers: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateSavedNavLinkDto {
  tenantId?: string;
  userId?: string;
  scope: NavLinkScope;
  name: string;
  description?: string;
  icon?: string;
  sectionName?: string;
  sectionIcon?: string;
  targetPath: string;
  filterConfigJson?: string;
  columnConfigJson?: string;
  sortConfigJson?: string;
  sharedWithCustomerIds?: string[];
  sharedWithTeamIds?: string[];
  sharedWithUserIds?: string[];
  requiredPermission?: string;
  canBeModifiedByOthers?: boolean;
  displayOrder?: number;
}

export type UpdateSavedNavLinkDto = Omit<CreateSavedNavLinkDto, 'canBeModifiedByOthers'> & {
  id: string;
  canBeModifiedByOthers?: boolean;
};

// Saved Views
export type SavedViewSharingMode = 'private' | 'public' | 'custom';

export interface SavedView {
  id: string;
  tenantId: string;
  ownerUserId: string;
  tableType: string;
  name: string;
  description?: string;
  isDefault: boolean;
  isPublic: boolean;
  filterConfigJson: string;
  columnConfigJson: string;
  groupByConfigJson?: string;
  isExpanded?: boolean;
  sharedWithUserIds?: string[];
  sharedWithTeamIds?: string[];
  sharedWithCustomerIds?: string[];
  createdAt: string;
  updatedAt?: string;
}

export interface CreateSavedViewDto {
  tableType: string;
  name: string;
  description?: string;
  isDefault: boolean;
  isPublic: boolean;
  filterConfigJson: string;
  columnConfigJson: string;
  groupByConfigJson?: string;
  isExpanded?: boolean;
  sharedWithUserIds?: string[];
  sharedWithTeamIds?: string[];
  sharedWithCustomerIds?: string[];
}

export type UpdateSavedViewDto = Omit<CreateSavedViewDto, 'tableType'>;

// AI Resolution Suggestion types
export interface ResolutionSuggestionDto {
  id: string;
  incidentId: string;
  description: string;
  targetCIs: string | null;
  errorMessage: string | null;
  rootCauseHypothesis: string | null;
  summary: string;
  suggestedSolution: string | null;
  missingInformation: string | null;
  confidence: number;
  confidenceReason: string | null;
  modelName: string;
  totalTokens: number;
  creditsCharged: number;
  similarIncidentsFound: number;
  generatedAt: string;
  isHelpful: boolean | null;
  feedbackComment: string | null;
  /**
   * Server-authored, factual warnings about omitted/unsupported/truncated evidence
   * (e.g. unreadable attachments, truncated ticket sections). A single string that may
   * contain multiple newline-separated warning lines (verified against the backend
   * `ResolutionSuggestionDto.ContextWarnings` — a scalar `string?`, NOT an array; only
   * the private chat's evidence-snapshot `contextWarnings` is an array). Plain text
   * only — never render as HTML/Markdown. Optional because older cached suggestions
   * and not-yet-updated backend responses may omit this field; always treat
   * `undefined` the same as `null` (no warnings), never as an error.
   */
  contextWarnings?: string | null;
  /**
   * The incident's customer at the time this suggestion was generated. Optional for
   * the same reason as `contextWarnings` above — legacy rows and in-flight backend
   * rollout may not populate it yet. Used defensively by the frontend only for display;
   * eligibility/staleness enforcement is a backend concern.
   */
  customerIdAtGeneration?: string | null;
}

export interface ResolutionSuggestionFeedbackRequest {
  isHelpful: boolean;
  comment?: string;
}

export interface GetResolutionSuggestionOptions {
  /** Bypasses cache and forces a fresh generation. Omit/false for ordinary Generate. */
  forceRegenerate?: boolean;
  /**
   * Optional technician instructions for this generation only (max 2,000 chars).
   * Private: never echoed back in the suggestion DTO or shared comments. Trimmed and
   * omitted from the request body entirely when empty.
   */
  additionalInstructions?: string;
}

// AI resolution-suggestion private follow-up chat
// Backend contract (agreed, not yet implemented in this change):
//   POST /api/tenants/{t}/incidents/{i}/ai-conversations            body { suggestionId }
//   GET  /api/tenants/{t}/incidents/{i}/ai-conversations/{id}
//   POST /api/tenants/{t}/incidents/{i}/ai-conversations/{id}/messages  body { requestId, content }
export type IncidentAiMessageRole = 'user' | 'assistant';

export interface IncidentAiMessageDto {
  id: string;
  requestId: string;
  content: string;
  role: IncidentAiMessageRole;
  status: string;
  creditsCharged: number;
  createdAt: string;
  eventsRetrievedAtUtc?: string | null;
  eventSourceIds?: string[] | null;
  contextWarnings?: string[] | null;
  eventsScanned?: number | null;
  eventExcerptsIncluded?: number | null;
  eventEvidence?: IncidentAiMessageEventEvidenceDto | null;
}

export interface IncidentAiMessageEventEvidenceDto {
  searchedAtUtc?: string | null;
  retrievalCompletedAtUtc?: string | null;
  includedCount?: number | null;
  searchedCount?: number | null;
  omittedCount?: number | null;
  warnings?: string[] | null;
  provenance?: string[] | null;
}

export interface IncidentAiConversationDto {
  id: string;
  suggestionId: string;
  /** When the grounding evidence snapshot was captured — distinct from message timestamps. */
  evidenceCapturedAt: string;
  /** Fixed 90-day retention boundary; does not extend when new messages are sent. */
  expiresAt: string;
  /** Successful turns used so far, out of a 50-turn maximum. */
  turnsUsed: number;
  /** Factual warnings about this conversation's evidence snapshot, one entry per warning. Plain text only. */
  contextWarnings?: string[] | null;
  allowance?: IncidentAiChatAllowanceDto | null;
  messages: IncidentAiMessageDto[];
}

export interface SendIncidentAiMessageRequest {
  requestId: string;
  content: string;
}

export interface IncidentAiChatAllowanceDto {
  tenantId: string;
  incidentId: string;
  technicianUserId: string;
  effectiveLimit: number;
  successfulAnswersUsed: number;
  remaining: number;
  hasTechnicianOverride: boolean;
  technicianOverrideLimit: number | null;
  tenantDefaultLimit: number;
  tierCeiling: number;
  tier: string;
  limitSource: 'tier-ceiling' | 'tenant-default' | 'technician-override';
  isExhausted: boolean;
  hasPendingRequest: boolean;
  pendingRequestId: string | null;
  pendingStartedAtUtc: string | null;
  pendingLeaseExpiresAtUtc: string | null;
}

export interface IncidentAiTechnicianAllowanceSummaryDto {
  technicianUserId: string;
  technicianDisplayName: string;
  technicianIsActive: boolean;
  effectiveLimit: number;
  successfulAnswersUsed: number;
  remaining: number;
  hasTechnicianOverride: boolean;
  technicianOverrideLimit: number | null;
  tenantDefaultLimit: number;
  tierCeiling: number;
  hasPendingRequest: boolean;
  pendingLeaseExpiresAtUtc: string | null;
}

export interface IncidentAiChatAllowanceResetRequest {
  requestId: string;
  reason: string;
}

export interface IncidentAiChatAllowanceResetResultDto {
  tenantId: string;
  incidentId: string;
  technicianUserId: string;
  requestId: string;
  applied: boolean;
  idempotentReplay: boolean;
  successfulAnswersUsedBefore: number;
  successfulAnswersUsedAfter: number;
  resetGenerationAfter: number;
  resetAtUtc: string;
}

export interface IncidentAiChatTenantSettingsDto {
  /** Tenant default before the tier ceiling: the tenant override when set, otherwise the platform default. */
  defaultLimit: number;
  /** Per-tenant override; null means the tenant inherits the platform default. */
  tenantOverrideLimit: number | null;
  platformDefaultLimit: number;
  inheritsPlatformDefault: boolean;
  tierCeiling: number;
  tier: string;
  effectiveDefaultLimit: number;
}

export interface IncidentAiChatTenantSettingsUpdateRequest {
  /** A number sets the tenant override; null removes it so the tenant inherits the platform default. */
  defaultLimit: number | null;
}

export interface IncidentAiChatTechnicianOverrideDto {
  userId: string;
  limit: number | null;
  inheritsTenantDefault: boolean;
  effectiveLimit: number;
  tenantDefaultLimit: number;
  tierCeiling: number;
}

export interface IncidentAiChatTechnicianOverrideUpdateRequest {
  limit: number | null;
}

// AI Credits types
export interface AiCreditBalanceDto {
  totalCreditsUsed: number;
  reservedCredits?: number;
  totalTokensConsumed: number;
  totalEstimatedCost: number;
  monthlyLimit: number | null;
  remainingCredits: number | null;
  isAiEnabled: boolean;
  effectivePricePerCredit: number;
  totalCustomerCost: number;
  reservedCustomerCostUsd?: number;
  monthlyCostCapUsd: number | null;
  remainingCostBudgetUsd: number | null;
  monthlyProviderCostCapUsd?: number;
  remainingProviderCostBudgetUsd?: number;
  reservedProviderCostUsd?: number;
  totalActualCost: number;
  tier?: string;
  isTrialEligible?: boolean;
  entitlementMonthlyCredits?: number;
  monthlyCreditsOperatingLimit?: number | null;
  entitlementMonthlyProviderCostCapUsd?: number;
  monthlyProviderCostCapOperatingLimitUsd?: number | null;
  quotaUpdatedAtUtc?: string | null;
  aiUnavailableReasonCode?: string | null;
  aiUnavailableReason?: string | null;
  isResolutionSuggestionsEnabled: boolean;
  isBeaconEnabled: boolean;
  isKnowledgeSourcesEnabled: boolean;
  byOperation: AiCreditUsageByOperationDto[];
  byModel: AiCreditUsageByModelDto[];
  dailyBreakdown: AiCreditDailyUsageDto[];
}

export type AiCreditUsageSummaryDto = AiCreditBalanceDto;

export interface AiCreditUsageByOperationDto {
  operationType: string;
  credits: number;
  tokens: number;
  requests: number;
  estimatedCost: number;
}

export interface AiCreditUsageByModelDto {
  modelName: string;
  credits: number;
  tokens: number;
  requests: number;
}

export interface AiCreditDailyUsageDto {
  date: string;
  credits: number;
  tokens: number;
  requests: number;
}

export interface UpdateTenantAiSettingsRequest {
  enableAiResolutionSuggestions?: boolean;
  enableAiBeacon?: boolean;
  enableAiKnowledgeSources?: boolean;
  monthlyCreditsLimit?: number;
  monthlyCostCapUsd?: number;
  monthlyCreditsOperatingLimit?: number | null;
  monthlyProviderCostCapOperatingLimitUsd?: number | null;
  overrideReason?: string;
  expectedQuotaUpdatedAtUtc?: string;
}

// AI Knowledge Source types
export interface AiKnowledgeSourceDto {
  id: string;
  sourceType: string;
  name: string;
  description?: string;
  isEnabled: boolean;
  displayOrder: number;
}

export interface UpsertAiKnowledgeSourceRequest {
  sourceType: string;
  name: string;
  description?: string;
  configuration?: string;
  isEnabled: boolean;
  displayOrder?: number;
}

// Cross-tenant AI credits overview (system admin)
export interface SystemAiCreditsOverviewDto {
  totalCreditsUsedAllTenants: number;
  totalTokensAllTenants: number;
  totalEstimatedCost: number;
  totalActualCost: number;
  totalTransactionsAllTenants: number;
  tenantBreakdown: TenantAiCreditSummaryDto[];
}

export interface TenantAiCreditSummaryDto {
  tenantId: string;
  tenantName: string;
  monthlyCostCapUsd: number | null;
  creditsUsed: number;
  creditsRemaining: number;
  totalTokens: number;
  estimatedCost: number;
  totalCustomerCost: number;
  totalActualCost: number;
  remainingCostBudgetUsd: number | null;
}
